import React, { useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useApolloClient, useMutation, ApolloError } from '@apollo/client';
import { useIntl } from 'react-intl';
import SignupFormComponent from '@fuww/library/src/Users/SignupForm';
import { useCaptcha } from '../Captcha';
import ServerValidation from '../ServerValidation';
import { languageOptions } from '../../data/marketplace';
import registerMutation from '../../lib/mutations/register.graphql';
import messages from '../../lib/messages.json';
import { handleUserRegistration } from './Signup';
import getEmailSuggestionFormatter from '../../lib/getEmailSuggestionFormatter';
import loginMutation from '../../lib/mutations/login.graphql';
import onLogin from '../../lib/onLogin';
import { useSiteContext } from '../SiteContext';
import { Registrar } from '../../lib/graphql/api/graphql';
import createAnalyticsEventMutation
  from '../../lib/mutations/createAnalyticsEvent.graphql';
import CountrySelect from '../CountrySelect';

const signupFormPropertyTypes = {
  isBuyer: PropTypes.bool,
  registeredVia: PropTypes.oneOf(Object.values(Registrar)).isRequired,
};

type SignupFormProperties = InferProps<typeof signupFormPropertyTypes>;

const SignupForm = ({
  isBuyer = false,
  registeredVia,
}: SignupFormProperties) => {
  const intl = useIntl();
  const {
    locale,
    prefix,
    hasLocalNews,
    url,
    newsboard,
  } = useSiteContext();

  const [submitErrors, setSubmitErrors] = useState<ApolloError | null>(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const client = useApolloClient();
  const { executeRecaptcha } = useCaptcha();
  const [createAnalyticsEvent] = useMutation(createAnalyticsEventMutation);

  const [createSession] = useMutation(loginMutation, {
    onCompleted: (data) => {
      onLogin(
        data,
        setSubmitDisabled,
        intl.formatMessage(messages['register.successHeadline']),
        client,
        'RegisterPage',
        createAnalyticsEvent,
        prefix,
        hasLocalNews,
        url,
        newsboard,
      );
    },
  });

  const [registerUser] = useMutation(registerMutation, {
    onCompleted: (_, options) => {
      const email = options?.variables?.email;
      const password = options?.variables?.password;
      window.plausible('Signup', {
        props: {
          provider: 'EmailAndPassword',
          isBuyer,
        },
      });
      createSession({
        variables: {
          username: email,
          password,
        },
      });
    },
    onError: (error) => {
      setSubmitErrors(error);
      setSubmitDisabled(false);
    },
  });

  const submitHandler = async ({
    firstName,
    lastName,
    email,
    password,
    chamberOfCommerceNumber,
    companyName,
    country,
    language,
    websiteUrl,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    chamberOfCommerceNumber?: string;
    companyName?: string;
    country?: string;
    language?: string;
    websiteUrl?: string;
  }) => {
    setSubmitDisabled(true);
    setSubmitErrors(null);

    const buyerSpecificFields = isBuyer
      ? {
        chamberOfCommerceNumber,
        companyName,
        country,
        language,
        websiteUrl,
      } : {};

    handleUserRegistration(
      registerUser,
      {
        firstName,
        lastName,
        email,
        password,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        captchaResponse: await executeRecaptcha!('register'),
        registeredVia,
        ...buyerSpecificFields,
      },
      locale,
    );
  };

  return (
    <SignupFormComponent
      firstNameLabel={intl.formatMessage(messages.firstName)}
      lastNameLabel={intl.formatMessage(messages.lastName)}
      emailAddressLabel={intl.formatMessage(messages.emailAddress)}
      passwordLabel={intl.formatMessage(messages.password)}
      companyNameLabel={
        isBuyer ? intl.formatMessage(messages.companyName) : undefined
      }
      websiteUrlLabel={
        isBuyer ? intl.formatMessage(messages.website) : undefined
      }
      chamberOfCommerceNumberLabel={
        isBuyer ? intl.formatMessage(messages.chamberOfCommerce) : undefined
      }
      CountryField={isBuyer ? CountrySelect : undefined}
      languageOptions={languageOptions}
      requiredValidationMessage={intl.formatMessage(
        messages['applicantForm.fieldRequired'],
      )}
      emailValidationMessage={intl.formatMessage(
        messages['applicantForm.mustBeValidEmailAddress'],
      )}
      passwordTooShortMessage={intl.formatMessage(
        messages['register.passwordTooShort'],
      )}
      urlValidationMessage={
        isBuyer ? intl.formatMessage(messages.mustBeValidUrl) : undefined
      }
      submitLabel={intl.formatMessage(messages.register)}
      formatEmailSuggestion={getEmailSuggestionFormatter(intl)}
      submitHandler={submitHandler}
      serverValidation={
        submitErrors ? <ServerValidation errors={submitErrors} /> : null
      }
      submitDisabled={submitDisabled}
    />
  );
};

SignupForm.propTypes = signupFormPropertyTypes;

export default SignupForm;
